import React, { useMemo, useState } from "react";
import "./Categories.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faUserShield,
  faCloud,
  faGamepad,
  faLock,
  faMagnifyingGlass,
  faMobileScreen,
  faArrowRight,
  faCircleInfo,
  faLaptop,
  faGlobe,
  faSliders,
  faXmark,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";

const ALL_TAG = "All topics";

/** NEW content (more “Help Center” + practical) */
const RAW_POSTS = [
  {
    id: "01",
    title: "Which plan fits me? (device count + use case)",
    excerpt:
      "A quick way to choose: 1 device vs multi-device, VPN need, and family coverage—without paying for extras you won’t use.",
    category: "Plan picking",
    icon: faCircleInfo,
    read: "3 min",
  },
  {
    id: "02",
    title: "Antivirus vs Internet Security vs ‘360’ suites",
    excerpt:
      "Understand what’s included at each level (firewall, VPN, backup, password tools) and when upgrades actually matter.",
    category: "Plan picking",
    icon: faShieldAlt,
    read: "5 min",
  },
  {
    id: "03",
    title: "Activation checklist after purchase (no confusion)",
    excerpt:
      "Download the official installer, sign in, apply your key, and confirm the subscription is active—step by step.",
    category: "Setup & activation",
    icon: faBolt,
    read: "4 min",
  },
  {
    id: "04",
    title: "VPN explained for normal people",
    excerpt:
      "When a VPN helps (public Wi-Fi), when it doesn’t (phishing), and what auto-connect means on phones and laptops.",
    category: "Privacy & VPN",
    icon: faGlobe,
    read: "4 min",
  },
  {
    id: "05",
    title: "Safe banking & shopping: 60-second browser checks",
    excerpt:
      "A simple checklist: URL, padlock, warnings, and what to do if your suite shows a red alert during payment.",
    category: "Safe browsing",
    icon: faLock,
    read: "3 min",
  },
  {
    id: "06",
    title: "Keep your PC fast while staying protected",
    excerpt:
      "Schedule scans, reduce popups, manage startup apps, and avoid “cleaner” tools that break things.",
    category: "Performance",
    icon: faLaptop,
    read: "4 min",
  },
  {
    id: "07",
    title: "Phone protection: Android vs iPhone differences",
    excerpt:
      "Why mobile protection looks different, what permissions matter, and which features vary by OS and region.",
    category: "Mobile",
    icon: faMobileScreen,
    read: "5 min",
  },
  {
    id: "08",
    title: "Family devices: shared Wi-Fi and multiple logins",
    excerpt:
      "How to cover multiple devices, share safely, and keep kids’ devices clean without constant popups.",
    category: "Family",
    icon: faUserShield,
    read: "4 min",
  },
  {
    id: "09",
    title: "Backup basics: don’t lose photos or work",
    excerpt:
      "Local vs cloud, how often to run it, and how to do a quick restore test so you’re never surprised.",
    category: "Backup",
    icon: faCloud,
    read: "5 min",
  },
  {
    id: "10",
    title: "Gaming mode: protection without FPS drops",
    excerpt:
      "Quiet mode, notification control, and scan timing so you stay protected without interruptions.",
    category: "Gaming",
    icon: faGamepad,
    read: "3 min",
  },
];

const TAGS = [
  ALL_TAG,
  "Plan picking",
  "Setup & activation",
  "Privacy & VPN",
  "Safe browsing",
  "Performance",
  "Mobile",
  "Family",
  "Backup",
  "Gaming",
];

const Categories = () => {
  const [activeTag, setActiveTag] = useState(ALL_TAG);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const txt = q.trim().toLowerCase();
    return RAW_POSTS.filter((p) => {
      const matchesTag = activeTag === ALL_TAG || p.category === activeTag;
      const matchesQ =
        !txt ||
        p.title.toLowerCase().includes(txt) ||
        p.excerpt.toLowerCase().includes(txt) ||
        p.category.toLowerCase().includes(txt);
      return matchesTag && matchesQ;
    });
  }, [activeTag, q]);

  const clearAll = () => {
    setActiveTag(ALL_TAG);
    setQ("");
  };

  return (
    <section className="hc" aria-labelledby="hc-title">
      <div className="hc-wrap">
        {/* Top header */}
        <header className="hc-head">
          <div className="hc-headCopy">
            <span className="hc-kicker">
              <FontAwesomeIcon icon={faSliders} />
              Help Center
            </span>
            <h2 id="hc-title" className="hc-title">
              Quick guides for choosing, activating, and using your{" "}
              <span>security software</span>
            </h2>
            <p className="hc-sub">
              Short, practical reads—built for normal users. Filter by topic or
              search by keyword like <strong>“VPN”</strong>,{" "}
              <strong>“activation”</strong>, or <strong>“slow laptop”</strong>.
            </p>
          </div>

          <div className="hc-stats" role="status" aria-live="polite">
            <div className="hc-count">
              Showing <strong>{filtered.length}</strong> guides
            </div>

            {(activeTag !== ALL_TAG || q.trim()) && (
              <button className="hc-clear" type="button" onClick={clearAll}>
                <FontAwesomeIcon icon={faXmark} />
                Clear filters
              </button>
            )}
          </div>
        </header>

        {/* Layout: Sidebar + Grid */}
        <div className="hc-layout">
          {/* Sidebar */}
          <aside className="hc-side" aria-label="Filter guides">
            <div className="hc-search">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder='Search: "VPN", "activation", "backup"...'
                aria-label="Search help center guides"
              />
            </div>

            <div className="hc-tags" role="tablist" aria-label="Topics">
              {TAGS.map((t) => (
                <button
                  key={t}
                  className={`hc-tag ${activeTag === t ? "active" : ""}`}
                  role="tab"
                  aria-selected={activeTag === t}
                  type="button"
                  onClick={() => setActiveTag(t)}
                >
                  {t}
                  {activeTag === t && <span className="hc-dot" aria-hidden="true" />}
                </button>
              ))}
            </div>

            <div className="hc-sideNote">
              Tip: pick <strong>Setup & activation</strong> if you want quick steps
              after purchase.
            </div>
          </aside>

          {/* Cards */}
          <div className="hc-grid" role="list">
            {filtered.map(({ id, title, excerpt, category, icon, read }) => (
              <article className="hc-card" key={id} role="listitem">
                <div className="hc-cardTop">
                  <span className="hc-badge">{category}</span>
                  <span className="hc-read">{read}</span>
                </div>

                <div className="hc-ico" aria-hidden="true">
                  <FontAwesomeIcon icon={icon} />
                </div>

                <h3 className="hc-h3">{title}</h3>
                <p className="hc-ex">{excerpt}</p>

                <div className="hc-actions">
                  <button
                    className="hc-link"
                    type="button"
                    onClick={() => window.alert(`Guide coming soon: ${title}`)}
                    aria-label={`Open guide: ${title}`}
                  >
                    Open guide <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </article>
            ))}

            {filtered.length === 0 && (
              <div className="hc-empty" role="status">
                <div className="hc-emptyTitle">No matches</div>
                <div className="hc-emptyText">
                  Try keywords like <strong>VPN</strong>, <strong>activation</strong>,{" "}
                  <strong>backup</strong>, or switch topics.
                </div>
                <button className="hc-clear" type="button" onClick={clearAll}>
                  <FontAwesomeIcon icon={faXmark} />
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Categories;
